import React, { useState, useRef } from 'react';
import { createMaintenance, deleteMaintenance, updateMaintStatus } from '../services/api';
import jsPDF from 'jspdf';

const STATUS_STYLE = {
  planned:     { bg: '#DBEAFE', color: '#1D4ED8', label: 'Planifiée'  },
  in_progress: { bg: '#FEF3C7', color: '#92400E', label: 'En cours'   },
  completed:        { bg: '#D1FAE5', color: '#065F46', label: 'Terminée'   },
  cancelled:   { bg: '#F1F5F9', color: '#94A3B8', label: 'Annulée'    },
};

const EMPTY_FORM = {
  title: '', description: '', service_id: '',
  planned_at: '', duration_min: 60, team: '',
};

const EMPTY_PDF_FORM = {
  end_date: '', technicien: '', travaux: '', pieces: '', duree: '', observations: '',
};

function formatDate(raw, opts = { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) {
  if (!raw) return '—';
  try { return new Date(raw).toLocaleString('fr-FR', opts); } catch { return '—'; }
}

export default function CalendarView({ maintenances = [], services = [], onCreated, onDeleted, onStatusUpdated }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [errors, setErrors]           = useState({});
  const [saving, setSaving]           = useState(false);
  const [apiError, setApiError]       = useState('');

  const [deleteModal, setDeleteModal]      = useState(false);
  const [maintToDelete, setMaintToDelete]  = useState(null);
  const [deleting, setDeleting]            = useState(false);
  const [deleteError, setDeleteError]      = useState('');

  const [pdfModal, setPdfModal]   = useState(false);
  const [pdfMaint, setPdfMaint]   = useState(null);
  const [pdfForm, setPdfForm]     = useState(EMPTY_PDF_FORM);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [pdfFiles, setPdfFiles]   = useState([]);
  const fileInputRef              = useRef(null);

  const [statusLoading, setStatusLoading] = useState({});

  const [reports, setReports] = useState(() => {
    try { return JSON.parse(localStorage.getItem('maintenance_reports') || '{}'); }
    catch { return {}; }
  });

  const year        = currentDate.getFullYear();
  const month       = currentDate.getMonth();
  const today       = new Date();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay    = new Date(year, month, 1).getDay();
  const offset      = firstDay === 0 ? 6 : firstDay - 1;
  const monthName   = currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const byDay = {};
  maintenances.forEach((m) => {
    const raw = m.planned_at || m.scheduled_at;
    if (!raw) return;
    const d = new Date(raw);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(m);
    }
  });

  const handleDayClick = (day) => {
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${year}-${pad(month + 1)}-${pad(day)}T09:00`;
    setForm({ ...EMPTY_FORM, planned_at: dateStr });
    setErrors({});
    setApiError('');
    setSelectedDay(day);
  };

  const set = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const setPdf = (key, value) => setPdfForm(prev => ({ ...prev, [key]: value }));

  const validate = () => {
    const e = {};
    if (!form.title.trim())    e.title      = 'Le titre est requis';
    if (!form.service_id)      e.service_id = 'Le service est requis';
    if (!form.planned_at)      e.planned_at = 'La date est requise';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setApiError('');
    try {
      const res = await createMaintenance(form);
      onCreated && onCreated(res.data.data || res.data);
      setSelectedDay(null);
    } catch (err) {
      setApiError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (m) => {
    setMaintToDelete(m);
    setDeleteError('');
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!maintToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteMaintenance(maintToDelete.id);
      onDeleted && onDeleted(maintToDelete.id);
      setDeleteModal(false);
      setMaintToDelete(null);
    } catch (err) {
      setDeleteError(err.response?.data?.message || err.response?.data?.error || `Erreur ${err.response?.status || ''} lors de la suppression`);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (m, newStatus) => {
    if (statusLoading[m.id]) return;
    setStatusLoading(prev => ({ ...prev, [m.id]: true }));
    try {
      await updateMaintStatus(m.id, newStatus);
      onStatusUpdated && onStatusUpdated(m.id, newStatus);
      if (newStatus === 'completed') {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const localNow = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
        setPdfMaint({ ...m, status: 'completed' });
        setPdfForm({
          end_date: localNow,
          technicien: m.team || '',
          travaux: m.description || '',
          pieces: '',
          duree: String(m.duration_min || ''),
          observations: '',
        });
        setPdfModal(true);
      }
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setStatusLoading(prev => ({ ...prev, [m.id]: false }));
    }
  };

  const closePdfModal = async () => {
    if (pdfMaint) {
      try {
        await updateMaintStatus(pdfMaint.id, 'in_progress');
        onStatusUpdated && onStatusUpdated(pdfMaint.id, 'in_progress');
      } catch (err) {
        console.error('Revert status error:', err);
      }
    }
    setPdfModal(false);
    setPdfMaint(null);
    setPdfFiles([]);
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    setPdfFiles(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setPdfFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const generatePDF = async () => {
    if (!pdfMaint) return;
    setPdfSaving(true);
    // Capturer les valeurs avant tout await (les states peuvent changer)
    const filesCopy = [...pdfFiles];
    const maintId   = pdfMaint.id;
    const maintData = { ...pdfMaint };
    try {
      const doc = new jsPDF();
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      doc.setFillColor(27, 42, 74);
      doc.rect(0, 0, pageW, 36, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("RAPPORT D'INTERVENTION", pageW / 2, 16, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Généré le ${formatDate(new Date().toISOString())}`, pageW / 2, 27, { align: 'center' });

      doc.setFillColor(201, 168, 76);
      doc.rect(0, 36, pageW, 2, 'F');

      let y = 50;

      const sectionTitle = (title) => {
        doc.setTextColor(27, 42, 74);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(title, 15, y);
        y += 5;
        doc.setDrawColor(201, 168, 76);
        doc.setLineWidth(0.5);
        doc.line(15, y, pageW - 15, y);
        y += 8;
      };

      const labelValue = (label, value) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text(label + ' :', 15, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);
        doc.text(String(value || '—'), 72, y);
        y += 7;
      };

      const multiField = (label, value) => {
        if (!value?.trim()) return;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text(label + ' :', 15, y);
        y += 6;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);
        const lines = doc.splitTextToSize(value, pageW - 30);
        doc.text(lines, 15, y);
        y += lines.length * 5.5 + 6;
      };

      sectionTitle('INFORMATIONS DE LA MAINTENANCE');
      const dateRaw = maintData.planned_at || maintData.scheduled_at;
      labelValue('Titre', maintData.title);
      labelValue('Service', maintData.service_name);
      labelValue('Date planifiée', formatDate(dateRaw));
      labelValue('Équipe', maintData.team);
      y += 5;

      sectionTitle("DÉTAILS DE L'INTERVENTION");
      labelValue('Date de fin', formatDate(pdfForm.end_date));
      labelValue('Technicien', pdfForm.technicien);
      labelValue('Durée réelle', pdfForm.duree ? `${pdfForm.duree} min` : '—');
      y += 5;

      multiField('Description des travaux', pdfForm.travaux);
      multiField('Pièces remplacées', pdfForm.pieces);
      multiField('Observations', pdfForm.observations);

      if (pdfFiles.length > 0) {
        sectionTitle('DOCUMENTS JOINTS');
        pdfFiles.forEach((f, idx) => {
          const sizeMb = (f.size / 1024).toFixed(1);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(51, 65, 85);
          doc.text(`${idx + 1}. ${f.name}  (${sizeMb} Ko)`, 18, y);
          y += 6;
        });
        y += 4;
      }

      y += 5;
      doc.setFillColor(209, 250, 229);
      doc.roundedRect(15, y - 5, 65, 10, 2, 2, 'F');
      doc.setDrawColor(6, 95, 70);
      doc.setLineWidth(0.3);
      doc.roundedRect(15, y - 5, 65, 10, 2, 2, 'S');
      doc.setTextColor(6, 95, 70);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('TERMINÉE', 47.5, y + 1, { align: 'center' });

      doc.setFillColor(27, 42, 74);
      doc.rect(0, pageH - 15, pageW, 15, 'F');
      doc.setTextColor(201, 168, 76);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('MONITOR by RevealIT — Infrastructure Supervision Platform', pageW / 2, pageH - 5, { align: 'center' });

      const dataUri = doc.output('datauristring');

      let attachedFiles = [];
      try {
        attachedFiles = await Promise.all(
          filesCopy.map(async (f) => ({
            name: f.name,
            type: f.type,
            size: f.size,
            dataUri: await fileToBase64(f),
          }))
        );
      } catch (fileErr) {
        console.error('File conversion error:', fileErr);
      }

      const newReports = {
        ...reports,
        [maintId]: { dataUri, title: maintData.title, files: attachedFiles },
      };
      setReports(newReports);
      try { localStorage.setItem('maintenance_reports', JSON.stringify(newReports)); } catch {}
    } catch (err) {
      console.error('PDF generation error:', err);
    }
    setPdfModal(false);
    setPdfMaint(null);
    setPdfFiles([]);
    setPdfSaving(false);
  };

  const downloadPDF = (maintId, title) => {
    const r = reports[maintId];
    if (!r) return;
    const link = document.createElement('a');
    link.href = r.dataUri;
    link.download = `rapport_intervention_${title.replace(/[^a-zA-Z0-9]/g, '_')}_${maintId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadFile = (f) => {
    if (!f?.dataUri) return;
    try {
      const [header, b64] = f.dataUri.split(',');
      const mimeType = header.match(/:(.*?);/)[1];
      const byteChars = atob(b64);
      const byteArr = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArr[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([byteArr], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = f.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('File download error:', err);
    }
  };

  const isLocked = (m) => m.status === 'completed' || m.status === 'cancelled';

  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 600, color: '#64748B',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5,
  };
  const inputStyle = (hasError) => ({
    width: '100%', padding: '9px 12px', boxSizing: 'border-box',
    border: `1px solid ${hasError ? '#DC2626' : '#E2E8F0'}`,
    borderRadius: 8, fontSize: 13, outline: 'none',
    background: '#F8FAFC', color: '#1B2A4A',
  });
  const errMsg = { color: '#DC2626', fontSize: 11, marginTop: 3 };
  const field  = { marginBottom: 14 };

  return (
    <div>
      {/* En-tête + navigation mois */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#1B2A4A',
                      textTransform: 'uppercase', letterSpacing: 1,
                      display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 3, height: 12, background: '#C9A84C', borderRadius: 2 }} />
          Calendrier — {monthName}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={prevMonth}
            style={{ background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 6,
                     padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#1B2A4A' }}>
            ‹
          </button>
          <button onClick={() => setCurrentDate(new Date())}
            style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6,
                     padding: '4px 10px', cursor: 'pointer', fontSize: 11,
                     color: '#2563EB', fontWeight: 600 }}>
            Aujourd'hui
          </button>
          <button onClick={nextMonth}
            style={{ background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 6,
                     padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#1B2A4A' }}>
            ›
          </button>
        </div>
      </div>

      {/* Grille calendrier */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 12 }}>
        {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map((d) => (
          <div key={d} style={{ fontSize: 9, fontWeight: 600, color: '#94A3B8',
                                textAlign: 'center', padding: 4 }}>{d}</div>
        ))}
        {Array.from({ length: offset }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const isToday = day === today.getDate() &&
                          month === today.getMonth() &&
                          year === today.getFullYear();
          const hasMaint = (byDay[day] || []).length > 0;
          return (
            <div key={day}
              onClick={() => handleDayClick(day)}
              title="Cliquer pour planifier une maintenance"
              style={{
                background: isToday ? '#EEF3FB' : '#fff',
                border: isToday ? '1px solid #2563EB' : '1px solid #E2E8F0',
                borderRadius: 6, padding: 5, minHeight: 56,
                cursor: 'pointer', transition: 'box-shadow .15s',
                position: 'relative',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(37,99,235,.15)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ fontSize: 10, fontWeight: 600,
                            color: isToday ? '#2563EB' : '#1B2A4A',
                            marginBottom: 3 }}>{day}</div>
              {(byDay[day] || []).map((m, i) => {
                const st = STATUS_STYLE[m.status] || STATUS_STYLE.planned;
                return (
                  <div key={i} style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3,
                                        marginBottom: 2, background: st.bg, color: st.color,
                                        fontWeight: 500, overflow: 'hidden',
                                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                       title={`${m.title} — ${m.service_name} (${st.label})`}>
                    {m.service_name?.substring(0, 10)}
                  </div>
                );
              })}
              {!hasMaint && (
                <div style={{ position: 'absolute', bottom: 3, right: 4,
                              fontSize: 9, color: '#CBD5E1' }}>+</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        {Object.entries(STATUS_STYLE).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5,
                                fontSize: 10, color: '#64748B' }}>
            <div style={{ width: 10, height: 10, borderRadius: 3,
                          background: v.bg, border: `1px solid ${v.color}` }} />
            {v.label}
          </div>
        ))}
      </div>

      {/* Tableau maintenances */}
      <div style={{ fontSize: 11, fontWeight: 600, color: '#1B2A4A',
                    textTransform: 'uppercase', letterSpacing: 1,
                    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span style={{ width: 3, height: 12, background: '#C9A84C', borderRadius: 2 }} />
        Interventions planifiées
      </div>

      {maintenances.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 12, padding: 24 }}>
          Aucune maintenance planifiée
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff',
                        border: '1px solid #E2E8F0', borderRadius: 9,
                        overflow: 'hidden', fontSize: 11 }}>
          <thead>
            <tr style={{ background: '#1B2A4A' }}>
              {['Date & Heure','Service','Titre','Durée','Équipe','Statut','Rapport',''].map((h) => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                                     color: 'rgba(255,255,255,.8)', fontSize: 10,
                                     fontWeight: 600, textTransform: 'uppercase',
                                     letterSpacing: .5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {maintenances.map((m, i) => {
              const st = STATUS_STYLE[m.status] || STATUS_STYLE.planned;
              const dateRaw = m.planned_at || m.scheduled_at;
              const locked = isLocked(m);
              return (
                <tr key={m.id} style={{ borderBottom: '1px solid #F1F5F9',
                                        background: i % 2 === 0 ? '#fff' : '#F8FAFC' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0F6FF'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#F8FAFC'}>
                  <td style={{ padding: '8px 12px', color: '#334155' }}>
                    {dateRaw ? new Date(dateRaw).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                    }) : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#334155', fontWeight: 500 }}>
                    {m.service_name}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#334155' }}>{m.title}</td>
                  <td style={{ padding: '8px 12px', color: '#334155' }}>{m.duration_min} min</td>
                  <td style={{ padding: '8px 12px', color: '#334155' }}>{m.team || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {locked ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 9, fontWeight: 700, padding: '3px 8px',
                        borderRadius: 12, textTransform: 'uppercase',
                        background: st.bg, color: st.color,
                        border: `1px solid ${st.color}22`,
                      }}>
                        {st.label}
                      </span>
                    ) : (
                      <select
                        value={m.status || 'planned'}
                        onChange={e => handleStatusChange(m, e.target.value)}
                        disabled={statusLoading[m.id]}
                        style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 7px',
                          borderRadius: 8, border: `1px solid ${st.color}`,
                          background: st.bg, color: st.color,
                          cursor: statusLoading[m.id] ? 'wait' : 'pointer',
                          outline: 'none', appearance: 'none',
                          paddingRight: 22,
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2364748B'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 6px center',
                        }}
                      >
                        <option value="planned">Planifiée</option>
                        <option value="in_progress">En cours</option>
                        <option value="completed">Terminée</option>
                        <option value="cancelled">Annulée</option>
                      </select>
                    )}
                  </td>
                  <td style={{ padding: '6px 12px' }}>
                    {reports[m.id] ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {/* Bouton PDF */}
                        <button
                          onClick={() => downloadPDF(m.id, m.title)}
                          title="Télécharger le rapport PDF"
                          style={{
                            background: '#EFF6FF', border: '1px solid #BFDBFE',
                            color: '#2563EB', borderRadius: 6, padding: '4px 9px',
                            fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                          }}>
                          📄 Rapport PDF
                        </button>
                        {/* Boutons fichiers joints */}
                        {(reports[m.id].files || []).map((f, idx) => (
                          <button
                            key={idx}
                            onClick={() => downloadFile(f)}
                            title={`Télécharger ${f.name}`}
                            style={{
                              background: '#F8FAFC', border: '1px solid #E2E8F0',
                              color: '#475569', borderRadius: 6, padding: '4px 9px',
                              fontSize: 10, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              maxWidth: 160, overflow: 'hidden',
                            }}>
                            <span style={{ flexShrink: 0 }}>📎</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#CBD5E1', fontSize: 10 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <button
                      onClick={() => askDelete(m)}
                      title="Supprimer cette maintenance"
                      style={{ background: '#FEF2F2', border: '1px solid #FECACA',
                                color: '#DC2626', borderRadius: 6, padding: '4px 10px',
                                fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── Modal PDF rapport intervention ── */}
      {pdfModal && pdfMaint && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                   zIndex: 10000, padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) closePdfModal(); }}
        >
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
                        maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box',
                        boxShadow: '0 24px 64px rgba(0,0,0,.35)', position: 'relative' }}>

            {/* Header avec croix */}
            <div style={{ background: '#1B2A4A', borderRadius: '16px 16px 0 0', padding: '20px 24px',
                          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
                  📋 Rapport d'intervention
                </div>
                <div style={{ color: '#C9A84C', fontSize: 11, marginTop: 3 }}>
                  {pdfMaint.title} — {pdfMaint.service_name}
                </div>
              </div>
              <button
                onClick={closePdfModal}
                title="Fermer (le statut reviendra à En cours)"
                style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)',
                         borderRadius: 8, width: 32, height: 32, cursor: 'pointer',
                         color: '#fff', fontSize: 18, lineHeight: 1, flexShrink: 0,
                         display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 8,
                            padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#065F46',
                            display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}></span>
                Statut passé à <strong>Terminée</strong>. Renseignez les détails pour générer le rapport PDF.
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Date de fin *</label>
                  <input type="datetime-local" value={pdfForm.end_date}
                    onChange={e => setPdf('end_date', e.target.value)}
                    style={inputStyle(false)} />
                </div>
                <div>
                  <label style={labelStyle}>Technicien *</label>
                  <input type="text" placeholder="Nom du technicien"
                    value={pdfForm.technicien}
                    onChange={e => setPdf('technicien', e.target.value)}
                    style={inputStyle(false)} />
                </div>
              </div>

              <div style={field}>
                <label style={labelStyle}>Durée réelle (min)</label>
                <input type="number" min={0} placeholder="ex: 90"
                  value={pdfForm.duree}
                  onChange={e => setPdf('duree', e.target.value)}
                  style={inputStyle(false)} />
              </div>

              <div style={field}>
                <label style={labelStyle}>Description des travaux</label>
                <textarea rows={3} placeholder="Décrire les travaux effectués…"
                  value={pdfForm.travaux}
                  onChange={e => setPdf('travaux', e.target.value)}
                  style={{ ...inputStyle(false), resize: 'vertical' }} />
              </div>

              <div style={field}>
                <label style={labelStyle}>Pièces remplacées</label>
                <textarea rows={2} placeholder="Lister les pièces remplacées (facultatif)…"
                  value={pdfForm.pieces}
                  onChange={e => setPdf('pieces', e.target.value)}
                  style={{ ...inputStyle(false), resize: 'vertical' }} />
              </div>

              <div style={field}>
                <label style={labelStyle}>Observations</label>
                <textarea rows={2} placeholder="Remarques, recommandations…"
                  value={pdfForm.observations}
                  onChange={e => setPdf('observations', e.target.value)}
                  style={{ ...inputStyle(false), resize: 'vertical' }} />
              </div>

              {/* Zone pièces jointes */}
              <div style={field}>
                <label style={labelStyle}>Documents joints</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%', padding: '9px 12px', boxSizing: 'border-box',
                    border: '2px dashed #CBD5E1', borderRadius: 8, background: '#F8FAFC',
                    color: '#64748B', fontSize: 13, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'border-color .15s, background .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563EB'; e.currentTarget.style.background = '#EFF6FF'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.background = '#F8FAFC'; }}
                >
                  📎 Ajouter un rapport / fichier
                </button>

                {pdfFiles.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {pdfFiles.map((f, idx) => (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7,
                        padding: '6px 10px', fontSize: 12,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, overflow: 'hidden' }}>
                          <span style={{ fontSize: 14, flexShrink: 0 }}>📄</span>
                          <span style={{ color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.name}
                          </span>
                          <span style={{ color: '#94A3B8', fontSize: 10, flexShrink: 0 }}>
                            {(f.size / 1024).toFixed(1)} Ko
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer',
                                   color: '#94A3B8', fontSize: 16, lineHeight: 1, flexShrink: 0,
                                   padding: '0 2px' }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end',
                            paddingTop: 14, borderTop: '1px solid #F1F5F9' }}>
                <button
                  onClick={generatePDF}
                  disabled={pdfSaving}
                  style={{ padding: '9px 24px', borderRadius: 8, border: 'none',
                           background: pdfSaving ? '#93C5FD' : '#2563EB', color: '#fff',
                           fontSize: 13, fontWeight: 600,
                           cursor: pdfSaving ? 'not-allowed' : 'pointer',
                           display: 'flex', alignItems: 'center', gap: 6 }}>
                  {pdfSaving ? 'Enregistrement…' : '✅ Valider'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal confirmation suppression ── */}
      {deleteModal && maintToDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28,
                        width: '100%', maxWidth: 420,
                        boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1B2A4A', marginBottom: 10 }}>
              🗑️ Supprimer la maintenance
            </div>
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
                          padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#7C2D12' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{maintToDelete.title}</div>
              <div style={{ fontSize: 11, color: '#94A3B8' }}>
                {maintToDelete.service_name} —{' '}
                {maintToDelete.planned_at
                  ? new Date(maintToDelete.planned_at).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: 'long', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })
                  : '—'}
              </div>
            </div>
            <div style={{ color: '#64748B', fontSize: 13, marginBottom: 20 }}>
              Cette action est irréversible. Confirmer la suppression ?
            </div>

            {deleteError && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
                            padding: '10px 14px', color: '#DC2626', fontSize: 12, marginBottom: 14 }}>
                ❌ {deleteError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDeleteModal(false); setMaintToDelete(null); }}
                disabled={deleting}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #E2E8F0',
                         background: '#fff', fontSize: 13, cursor: 'pointer', color: '#64748B' }}>
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ padding: '9px 20px', borderRadius: 8, border: 'none',
                         background: deleting ? '#FCA5A5' : '#DC2626', color: '#fff',
                         fontSize: 13, fontWeight: 600,
                         cursor: deleting ? 'not-allowed' : 'pointer' }}>
                {deleting ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal création maintenance ── */}
      {selectedDay !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28,
                        width: '100%', maxWidth: 500, maxHeight: '90vh',
                        overflowY: 'auto', boxSizing: 'border-box', position: 'relative',
                        boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>

            <button onClick={() => setSelectedDay(null)} style={{
              position: 'absolute', top: 14, right: 14, width: 30, height: 30,
              borderRadius: 8, background: '#F1F5F9', border: 'none',
              fontSize: 18, cursor: 'pointer', color: '#64748B',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>

            <div style={{ marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #F1F5F9' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1B2A4A' }}>
                📅 Planifier une maintenance
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                {new Date(year, month, selectedDay).toLocaleDateString('fr-FR', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })}
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={field}>
                <label style={labelStyle}>Titre *</label>
                <input type="text" placeholder="ex: Upgrade Amplitude 11.7"
                  value={form.title} onChange={e => set('title', e.target.value)}
                  style={inputStyle(errors.title)} />
                {errors.title && <div style={errMsg}>{errors.title}</div>}
              </div>

              <div style={field}>
                <label style={labelStyle}>Service *</label>
                <select value={form.service_id} onChange={e => set('service_id', e.target.value)}
                  style={{ ...inputStyle(errors.service_id), cursor: 'pointer', appearance: 'none' }}>
                  <option value="">— Sélectionner un service —</option>
                  {services.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {errors.service_id && <div style={errMsg}>{errors.service_id}</div>}
              </div>

              <div style={field}>
                <label style={labelStyle}>Date & Heure *</label>
                <input type="datetime-local" value={form.planned_at}
                  onChange={e => set('planned_at', e.target.value)}
                  style={inputStyle(errors.planned_at)} />
                {errors.planned_at && <div style={errMsg}>{errors.planned_at}</div>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Durée (min)</label>
                  <input type="number" min={5} value={form.duration_min}
                    onChange={e => set('duration_min', +e.target.value)}
                    style={inputStyle(false)} />
                </div>
                <div>
                  <label style={labelStyle}>Équipe</label>
                  <input type="text" placeholder="ex: CBS / Sopra"
                    value={form.team} onChange={e => set('team', e.target.value)}
                    style={inputStyle(false)} />
                </div>
              </div>

              <div style={field}>
                <label style={labelStyle}>Description</label>
                <textarea rows={3} placeholder="Détails de l'intervention…"
                  value={form.description} onChange={e => set('description', e.target.value)}
                  style={{ ...inputStyle(false), resize: 'vertical' }} />
              </div>

              {apiError && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA',
                              borderRadius: 8, padding: '10px 14px', color: '#DC2626',
                              fontSize: 12, marginBottom: 14 }}>
                  ❌ {apiError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end',
                            paddingTop: 14, borderTop: '1px solid #F1F5F9' }}>
                <button type="button" onClick={() => setSelectedDay(null)}
                  style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #E2E8F0',
                           background: '#fff', fontSize: 13, cursor: 'pointer', color: '#64748B' }}>
                  Annuler
                </button>
                <button type="submit" disabled={saving}
                  style={{ padding: '9px 24px', borderRadius: 8, border: 'none',
                           background: saving ? '#93C5FD' : '#2563EB', color: '#fff',
                           fontSize: 13, fontWeight: 600,
                           cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Enregistrement…' : 'Planifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
